import { get_coin_info } from "./aptos_sdk";
import {
  getPair,
  routeList,
  getBestTradeRoute,
  getBestTradeRouteOut,
} from "./swap_sdk";
export type CoinInfo = {
  decimals: number;
  symbol: string;
  address: string;
};

export type MyRewardInfo = {
  coin_info: CoinInfo;
  reward: number;
};

export type TradeRoute = {
  trade: Trade;
  pools: Pool[];
};

export type Pool = {
  reserve_x?: string;
  x_coin_info: CoinInfo;
  reserve_y?: string;
  y_coin_info: CoinInfo;
};

export type MyPoolVote = {
  pool: Pool;
  bribe_id: number;
  rewards: MyRewardInfo[]; // coin_type_info array
};

export type RewardAmount = {
  symbol: string;
  reward_amount: string;
};
export type PoolVote = {
  pool_addr: string;
  pool: Pool;
  my_votes: string;
  my_votes_percent: string;
  my_x_balance: string;
  my_y_balance: string;
  weight: string;
  weight_percent: string;
  reward_amounts: RewardAmount[];
};

export type Trade = {
  in_coin: string;
  in_amount: number;
  out_coin: string;
  out_amount: number;
};

export type StablePair = {
  reserve_x: { value: number };
  reserve_y: { value: number };
  decimals_x: number;
  decimals_y: number;
  decimals_lp: number;
  initial_a: number;
  future_a: number;
  initial_a_time: number;
  future_a_time: number;
  swap_fee: number;
  dao_fee: number;
  withdraw_fee: number;
};

export class StableCurve {
  static FEE_DENOMINATOR = 1000000;

  constructor(
    public xCoinInfo: CoinInfo,
    public yCoinInfo: CoinInfo,
    public stablePair: StablePair,
    public IsPairXtoY: boolean
  ) {}

  xUiBalance() {
    return (
      this.stablePair.reserve_x.value / Math.pow(10, this.xCoinInfo.decimals)
    );
  }

  yUiBalance() {
    return (
      this.stablePair.reserve_y.value / Math.pow(10, this.yCoinInfo.decimals)
    );
  }

  private static getA(
    initial_a: number,
    future_a: number,
    initial_a_time: number,
    future_a_time: number,
    timestamp: number
  ): number {
    if (timestamp < future_a_time) {
      if (future_a < initial_a) {
        return (
          initial_a -
          ((initial_a - future_a) * (timestamp - initial_a_time)) /
            (future_a_time - initial_a_time)
        );
      } else {
        return (
          initial_a +
          ((future_a - initial_a) * (timestamp - initial_a_time)) /
            (future_a_time - initial_a_time)
        );
      }
    } else {
      return future_a;
    }
  }

  private getCurrentA() {
    return StableCurve.getA(
      this.stablePair.initial_a,
      this.stablePair.future_a,
      this.stablePair.initial_a_time,
      this.stablePair.future_a_time,
      Date.now() * 1000
    );
  }

  private recurD(d: number, x: number, y: number, amp: number): number {
    const d1 =
      (8 * amp * x * y * (x + y) + 2 * d * d * d) /
      (3 * d * d + 4 * x * y * (2 * amp - 1));
    const minuend = d - d1;
    if (minuend <= 0.00000001) {
      return d1;
    } else {
      return this.recurD(d1, x, y, amp);
    }
  }

  private getD(x: number, y: number, amp: number) {
    const d0 = x + y;
    if (d0 === 0) {
      return d0;
    }
    return this.recurD(d0, x, y, amp);
  }

  private recurY(y: number, b: number, c: number, d: number): number {
    window.console.info("recurY", y, b, c, d);

    const yNext = (y * y + c) / (2 * y + b - d);
    let difference;
    if (yNext > y) difference = yNext - y;
    else difference = y - yNext;
    if (difference <= 0.00000001) return yNext;
    else {
      return this.recurY(yNext, b, c, d);
    }
  }

  private getY(x: number, amp: number, d: number) {
    if (d === 0) {
      return 0;
    }
    const y = d;
    const b = x + d / (2 * amp);
    const c = (d * d * d) / (8 * amp * x);
    return this.recurY(y, b, c, d);
  }

  private getPrice(x: number, y: number, d: number, a: number) {
    const dx = 0.001;
    const newy = this.getY(x - dx, a, d);
    return dx / (newy - y);
  }

  getCurrentPriceDirectional(isXtoY: boolean) {
    const x = this.xUiBalance();
    const y = this.yUiBalance();
    const a = this.getCurrentA();
    const d = this.getD(x, y, a);
    const xy = this.getPrice(x, y, d, a);
    if (isXtoY) {
      return { xToY: 1 / xy, yToX: xy };
    }
    return { xToY: xy, yToX: 1 / xy };
  }

  getQuoteDirectional(inputUiAmt: number, isXtoY: boolean) {
    const amp = this.getCurrentA();
    const d = this.getD(this.xUiBalance(), this.yUiBalance(), amp);
    let lhs, rhs, difference, inputSymbol, outputSymbol, amtFee, finalPrice;
    if (isXtoY) {
      inputSymbol = this.xCoinInfo.symbol;
      outputSymbol = this.yCoinInfo.symbol;
      lhs = inputUiAmt + this.xUiBalance();
      rhs = this.getY(lhs, amp, d);
      difference = this.yUiBalance() - rhs;
      amtFee =
        (difference * this.stablePair.swap_fee) / StableCurve.FEE_DENOMINATOR;
      finalPrice = this.getPrice(lhs, rhs, d, amp);
    } else {
      inputSymbol = this.xCoinInfo.symbol;
      outputSymbol = this.yCoinInfo.symbol;
      lhs = inputUiAmt + this.yUiBalance();
      rhs = this.getY(lhs, amp, d);
      difference = this.xUiBalance() - rhs;
      amtFee =
        (difference * this.stablePair.swap_fee) / StableCurve.FEE_DENOMINATOR;
      finalPrice = this.getPrice(lhs, rhs, d, amp);
    }
    const outputUiAmt = difference - amtFee;
    const initialPrice = this.getCurrentPriceDirectional(isXtoY).xToY;
    return {
      feeRate: this.stablePair.swap_fee / StableCurve.FEE_DENOMINATOR,
      inputSymbol,
      outputSymbol,
      inputUiAmt,
      amtFee: Number(amtFee.toFixed(8)),
      outputUiAmt: Number(outputUiAmt.toFixed(8)),
      initialPrice,
      avgPrice: (inputUiAmt / difference).toPrecision(8),
      finalPrice,
      priceImpact: Math.abs(finalPrice - initialPrice).toFixed(8),
    };
  }

  estimateWithdrawalOutput(
    lpUiAmount: number,
    lpSupplyUiAmt: number
  ): { xUiAmt: number; yUiAmt: number } {
    const fraction = lpUiAmount / lpSupplyUiAmt;
    return {
      xUiAmt: Number(
        (this.xUiBalance() * fraction).toFixed(this.xCoinInfo.decimals)
      ),
      yUiAmt: Number(
        (this.yUiBalance() * fraction).toFixed(this.yCoinInfo.decimals)
      ),
    };
  }

  estimateNeededYFromXDeposit(xUiAmt: number): number {
    const fraction = xUiAmt / this.xUiBalance();
    return Number(
      (this.yUiBalance() * fraction).toFixed(this.xCoinInfo.decimals)
    );
  }

  estimateNeededXFromYDeposit(yUiAmt: number): number {
    const fraction = yUiAmt / this.yUiBalance();
    return Number(
      (this.xUiBalance() * fraction).toFixed(this.yCoinInfo.decimals)
    );
  }

  getXDecimalsAmount(amount: number) {
    return Number(
      (amount / Math.pow(10, this.xCoinInfo.decimals)).toFixed(
        this.xCoinInfo.decimals
      )
    );
  }

  getYDecimalsAmount(amount: number) {
    return Number(
      (amount / Math.pow(10, this.yCoinInfo.decimals)).toFixed(
        this.xCoinInfo.decimals
      )
    );
  }

  getXIntAmountFromDecimals(amount: number): number {
    const inti = parseInt(
      (amount * Math.pow(10, this.xCoinInfo.decimals)).toFixed(0),
      10
    );
    window.console.info(
      "getXIntAmountFromDecimals",
      inti,
      this.xCoinInfo.decimals
    );
    return inti;
  }

  getYIntAmountFromDecimals(amount: number): number {
    const inti = parseInt(
      (amount * Math.pow(10, this.yCoinInfo.decimals)).toFixed(0),
      10
    );
    window.console.info(
      "getYIntAmountFromDecimals",
      inti,
      this.yCoinInfo.decimals
    );
    return inti;
  }
}

export function getTradeRouteOut(
  trade: Trade,
  allPairs: Pool[]
): TradeRoute | null {
  const routes: Pool[][] = [];
  routeList(
    allPairs,
    trade.in_coin,
    trade.out_coin,
    4,
    [],
    trade.in_coin,
    routes
  );
  const bestRoute = getBestTradeRouteOut(routes, trade);
  if (!bestRoute) return null;
  return {
    trade,
    pools: bestRoute,
  };
}

export function getTradeRoute(
  trade: Trade,
  allPairs: Pool[]
): TradeRoute | null {
  const routes: Pool[][] = [];
  routeList(
    allPairs,
    trade.in_coin,
    trade.out_coin,
    4,
    [],
    trade.in_coin,
    routes
  );
  const bestRoute = getBestTradeRoute(routes, trade);
  if (!bestRoute) return null;
  return {
    trade,
    pools: bestRoute,
  };
}
// [ x_coin, y_coin, z_coin, a_coin, b_coin]
export function getCoinListFromTradeRoute(tradeRoute: TradeRoute): string[] {
  const res: string[] = [];
  let in_coin = tradeRoute.trade.in_coin;
  for (let i = 0; i < tradeRoute.pools.length; i++) {
    const pool = tradeRoute.pools[i];
    res.push(in_coin);
    if (pool.x_coin_info.address === in_coin) {
      in_coin = pool.y_coin_info.address;
    } else {
      in_coin = pool.x_coin_info.address;
    }
  }
  res.push(in_coin);
  return res;
}

export async function getPool(
  coinX: string,
  coinY: string
): Promise<Pool | undefined> {
  let x = coinX;
  let y = coinY;
  if (coinX > coinY) {
    x = coinY;
    y = coinX;
  }
  try {
    const coin_address_x = x.split("::")[0];
    const coin_info_x = await get_coin_info(coin_address_x, x);

    const coin_address_y = y.split("::")[0];
    const coin_info_y = await get_coin_info(coin_address_y, y);
    const pair = await getPair(x, y);
    if (!pair) {
      return undefined;
    }

    const data: any = pair?.data;

    return {
      reserve_x: data.reserve_x,
      x_coin_info: {
        decimals: (coin_info_x!.data as any).decimals,
        symbol: (coin_info_x!.data as any).symbol,
        address: x,
      },
      reserve_y: data.reserve_y,
      y_coin_info: {
        decimals: (coin_info_y!.data as any).decimals,
        symbol: (coin_info_y!.data as any).symbol,
        address: y,
      },
    };
  } catch {}
  return undefined;
}
export const getShortCoinNameFromPool = (
  pool: Pool | undefined,
  coinAddress: string
): string => {
  if (!pool) return "";
  if (pool.x_coin_info.address === coinAddress) {
    return pool.x_coin_info.symbol;
  } else if (pool.y_coin_info.address === coinAddress) {
    return pool.y_coin_info.symbol;
  }
  return "";
};

export const getCoinInfo = async (coinAddress: string): Promise<CoinInfo> => {
  const coin_address = coinAddress.split("::")[0];
  const coin_info = await get_coin_info(coin_address, coinAddress);
  return {
    decimals: (coin_info!.data as any).decimals,
    symbol: (coin_info!.data as any).symbol,
    address: coinAddress,
  };
};
